"use client"

import * as React from "react"
import { Link2Icon, LockIcon, PiggyBankIcon } from "lucide-react"
import { moneyToNumberForChartGeometryOnly } from "@ibada/shared-types/finance"

import { Skeleton, cn } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { LoadingRegion, SkeletonCard, SkeletonLine, SkeletonPill } from "@/shared/components/skeletons"
import { AliveActionsCell, AliveMark, AlivePillTone, AliveStatusCell } from "@/shared/components/alive-cells"
import { ProgressRowTone, progressRowTone } from "@/shared/components/progress-rows"
import type { RowActionsMenuItem } from "@/shared/components/row-actions-menu"
import { formatMoney, isZeroOrNegativeMoney } from "@/features/finance/lib/finance-utils"
import { FinanceFundKind, FinanceFundStatus, type FinanceFundRecord } from "@/features/finance/model/finance-types"

const FUND_STATUS_PILL: Record<FinanceFundStatus, AlivePillTone> = {
  [FinanceFundStatus.Active]: AlivePillTone.Success,
  [FinanceFundStatus.Closed]: AlivePillTone.Danger,
  [FinanceFundStatus.Archived]: AlivePillTone.Neutral,
}

const BAR_CLASS: Record<ProgressRowTone, string> = {
  [ProgressRowTone.Default]: "bg-primary",
  [ProgressRowTone.Complete]: "bg-teal-600",
  [ProgressRowTone.Over]: "bg-destructive",
}

/*
 * The lead mark says what kind of fund it is before the name does: a system-linked fund mirrors an income
 * account (link, muted), a restricted fund carries a donor's condition (lock, amber), a church's own fund is a
 * piggy bank on a soft sky tint. No orange (the logo's) and no plain green (the app's actions).
 */
export function FundMark({ fund }: { fund: FinanceFundRecord }) {
  if (fund.kind === FinanceFundKind.SystemLinked) return <AliveMark icon={Link2Icon} />
  if (fund.isRestricted) {
    return <AliveMark icon={LockIcon} className='bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300' />
  }
  return <AliveMark icon={PiggyBankIcon} className='bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300' />
}

/** A decimal string with no non-zero digit ("0", "0.00", "-0.00"). Needs no currency and cannot throw. */
function isZeroBalance(balance: string): boolean {
  return !/[1-9]/.test(balance)
}

/** The goal: a thin bar with its % and "of <target>", or a muted line when the fund has no target. */
function FundGoal({ fund, currency }: { fund: FinanceFundRecord; currency: string }) {
  const t = useTranslations("finance.funds")
  const tProgress = useTranslations("common.progressRow")
  const target = fund.targetAmount
  if (target === null || isZeroOrNegativeMoney(target, currency)) {
    return <span className='text-muted-foreground text-xs font-semibold'>{t("noTarget")}</span>
  }
  // The bar's width is geometry (a pixel, not a cent); the figures beside it stay the exact strings.
  const percentage = Math.max(
    0,
    Math.round((moneyToNumberForChartGeometryOnly(fund.balance) / moneyToNumberForChartGeometryOnly(target)) * 100),
  )
  const tone = progressRowTone(percentage)
  return (
    <span className='flex flex-col gap-1'>
      <span className='flex items-baseline justify-between gap-2 text-xs tabular-nums'>
        <span data-tone={tone} className='font-extrabold'>
          {percentage} %
        </span>
        <span className='text-muted-foreground truncate font-semibold'>{tProgress("of", { amount: formatMoney(target, currency) })}</span>
      </span>
      <span className='bg-muted h-1.5 w-full overflow-hidden rounded-full'>
        <span className={cn("block h-full rounded-full", BAR_CLASS[tone])} style={{ width: `${Math.min(100, percentage)}%` }} />
      </span>
    </span>
  )
}

/**
 * One fund as the list shows it. Without the handlers (the create dialog's preview of a fund not saved yet) it
 * draws the same card as plain text: nothing to open, investigate or list.
 */
export function FundCard({
  fund,
  currency,
  label,
  onOpen,
  onInvestigate,
  onViewActivities,
  actionGroups = null,
}: {
  fund: FinanceFundRecord
  currency: string
  label: string
  onOpen?: (fund: FinanceFundRecord) => void
  onInvestigate?: (fund: FinanceFundRecord) => void
  onViewActivities?: (fund: FinanceFundRecord) => void
  /** Null for a system-linked fund: the engine maintains it, so it has no actions. */
  actionGroups?: RowActionsMenuItem[][] | null
}) {
  const t = useTranslations("finance.funds")
  const subtitle = [fund.kind === FinanceFundKind.SystemLinked ? t("systemManaged") : null, fund.description?.trim() || null]
    .filter(Boolean)
    .join(" · ")
  const count = fund.activityCount
  return (
    <li className='bg-card flex min-w-0 flex-col gap-3 rounded-2xl border p-4'>
      <span className='flex min-w-0 items-start gap-2.5'>
        <FundMark fund={fund} />
        <span className='flex min-w-0 flex-1 flex-col'>
          {onOpen ? (
            <button
              type='button'
              onClick={() => onOpen(fund)}
              className='truncate text-left text-[13.5px] font-extrabold hover:underline focus-visible:underline focus-visible:outline-none'
            >
              {label}
            </button>
          ) : (
            <span className='truncate text-[13.5px] font-extrabold'>{label}</span>
          )}
          {subtitle ? <span className='text-muted-foreground truncate text-xs font-semibold'>{subtitle}</span> : null}
        </span>
        {actionGroups ? (
          <AliveActionsCell label={t("actionsLabel")} openLabel={t("actions.open")} sheetHeader={label} groups={actionGroups} />
        ) : null}
      </span>
      {/* A fund's balance is a claim about restricted money: the transactions behind it are the proof. */}
      {onInvestigate ? (
        <button
          type='button'
          onClick={() => onInvestigate(fund)}
          className={cn(
            "self-start text-xl leading-tight font-extrabold tabular-nums underline decoration-dotted underline-offset-4 hover:decoration-solid",
            isZeroBalance(fund.balance) && "text-muted-foreground font-semibold",
          )}
        >
          {formatMoney(fund.balance, currency)}
        </button>
      ) : (
        <span
          className={cn(
            "self-start text-xl leading-tight font-extrabold tabular-nums",
            isZeroBalance(fund.balance) && "text-muted-foreground font-semibold",
          )}
        >
          {formatMoney(fund.balance, currency)}
        </span>
      )}
      <FundGoal fund={fund} currency={currency} />
      <span className='mt-auto flex flex-wrap items-center gap-1.5 pt-1'>
        <AliveStatusCell tone={fund.isRestricted ? AlivePillTone.Warning : AlivePillTone.Neutral}>
          {fund.isRestricted ? t("restrictionPill.restricted") : t("restrictionPill.unrestricted")}
        </AliveStatusCell>
        <AliveStatusCell tone={FUND_STATUS_PILL[fund.status] ?? AlivePillTone.Neutral}>
          {t(`status.${fund.status.toLowerCase()}` as never)}
        </AliveStatusCell>
        {/* "Activités liées 2": the count alone would not say what it counts. */}
        <span className='text-muted-foreground ml-auto text-xs font-semibold'>
          {t("fields.linkedActivities")}{" "}
          {count === 0 || !onViewActivities ? (
            <span className='tabular-nums'>{t("linkedActivities.count", { count })}</span>
          ) : (
            <button
              type='button'
              onClick={() => onViewActivities(fund)}
              aria-label={t("linkedActivities.view", { count })}
              className='text-foreground font-extrabold tabular-nums underline decoration-dotted underline-offset-4 hover:decoration-solid'
            >
              {t("linkedActivities.count", { count })}
            </button>
          )}
        </span>
      </span>
    </li>
  )
}

const GRID_CLASS = "grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"

/**
 * The funds on desktop as cards instead of a sparse wide table: a church has few of them, and each one is a
 * balance, a goal and a couple of facts (restriction, status, linked activities). The table's toolbar and
 * pager stay around it.
 */
export function FundCardGrid({
  funds,
  currency,
  isInitialLoading,
  emptyState,
  onOpen,
  onInvestigate,
  onViewActivities,
  actionGroups,
}: {
  funds: FinanceFundRecord[]
  currency: string
  isInitialLoading: boolean
  emptyState: React.ReactNode
  onOpen: (fund: FinanceFundRecord) => void
  onInvestigate: (fund: FinanceFundRecord) => void
  onViewActivities: (fund: FinanceFundRecord) => void
  actionGroups: (fund: FinanceFundRecord) => RowActionsMenuItem[][] | null
}) {
  if (isInitialLoading) {
    return (
      // The fund card's own layout: mark and name, balance, goal bar, pills.
      <LoadingRegion className={GRID_CLASS}>
        {Array.from({ length: 8 }, (_, index) => (
          <SkeletonCard key={index} className='flex min-w-0 flex-col gap-3 p-4'>
            <span className='flex items-start gap-2.5'>
              <Skeleton className='size-9 shrink-0 rounded-full' />
              <span className='flex min-w-0 flex-1 flex-col gap-2 pt-0.5'>
                <SkeletonLine className={cn("h-3.5", index % 2 === 0 ? "w-3/5" : "w-4/5")} />
                <SkeletonLine className='w-2/5' />
              </span>
            </span>
            <Skeleton className='h-6 w-32 rounded-lg' />
            <span className='flex flex-col gap-1.5'>
              <SkeletonLine className='w-full' />
              <Skeleton className='h-1.5 w-full rounded-full' />
            </span>
            <span className='mt-auto flex items-center gap-1.5 pt-1'>
              <SkeletonPill className='w-20' />
              <SkeletonLine className='ml-auto w-16' />
            </span>
          </SkeletonCard>
        ))}
      </LoadingRegion>
    )
  }
  if (funds.length === 0) return <div className='bg-card rounded-2xl border'>{emptyState}</div>
  return (
    <ul className={GRID_CLASS}>
      {funds.map((fund) => (
        <FundCard
          key={fund.id}
          fund={fund}
          currency={currency}
          label={fund.name}
          onOpen={onOpen}
          onInvestigate={onInvestigate}
          onViewActivities={onViewActivities}
          actionGroups={actionGroups(fund)}
        />
      ))}
    </ul>
  )
}
